import { savePastedInput } from '@/api/inputRecords';
import { type ClipboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Divider,
  Empty,
  Input,
  Result,
  Space,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { DeleteOutlined, InboxOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { apiBatchRowError, apiErrorCode, apiErrorMessage } from '@/api/client';
import {
  aiDetectBatchHeader,
  aiExtractApplication,
  createAiApplicationBatch,
  issueAiBatchToken,
  parseAiBatchWorkbook,
  type AiApplicationResult,
  type AiBatchHeaderDecision,
  type AiBatchWorkbookColumn,
  type AiBatchWorkbookResult,
  type CreateAiApplicationBatchResult,
} from '@/api/payouts';
import {
  aiHeaderSample,
  applyAiHeaderDecision,
  batchCurrencyTotals,
  buildAiBatchCreateInput,
  deriveBatchRowStatus,
  parseClipboardTableHtml,
  parsePastedTable,
  serializeOriginalRow,
  type AiBatchEditableRow,
  type ParsedPastedTable,
} from '@/domain/aiApplicationBatch';
import AiApplicationEditor from './AiApplicationEditor';

const { Paragraph, Text, Title } = Typography;
const MAX_AI_CONCURRENCY = 3;
const DEFINITE_REJECTION_CODES = new Set([
  'BAD_INPUT_SOURCE',
  'AI_BATCH_EMPTY',
  'AI_BATCH_TOO_MANY_ROWS',
  'BAD_AI_BATCH_CREATE',
  'AI_BATCH_TOKEN_CONFLICT',
  'AI_BATCH_ROW_INVALID',
  'PAYEE_VALIDATION_FAILED',
  'BAD_AMOUNT',
]);

function editableRows(result: Pick<AiBatchWorkbookResult, 'rows'>): AiBatchEditableRow[] {
  return result.rows.map((row) => ({
    ...row,
    status: row.unsafeCells.length > 0 ? 'needs_input' : 'pending',
    result: null,
    error: null,
    manualFields: new Set(),
    groupingConfirmed: !row.rowGroupingUncertain,
  }));
}

function statusTag(row: AiBatchEditableRow) {
  const status = deriveBatchRowStatus(row);
  if (status === 'ready') return <Tag color="success">可创建</Tag>;
  if (status === 'parsing') return <Tag color="processing">解析中</Tag>;
  if (status === 'failed') return <Tag color="error">解析失败</Tag>;
  if (status === 'needs_input') return <Tag color="warning">待确认</Tag>;
  return <Tag>等待解析</Tag>;
}

export default function AiBatchApplicationWorkspace() {
  const { message, modal } = App.useApp();
  const [pasteText, setPasteText] = useState('');
  const [pasteNotice, setPasteNotice] = useState<
    { kind: 'table'; rows: number; columns: number } | { kind: 'text' } | null
  >(null);
  const [columns, setColumns] = useState<AiBatchWorkbookColumn[]>([]);
  const [rows, setRows] = useState<AiBatchEditableRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const sourceRecordRef = useRef<string | undefined>(undefined);
  const [headerDecision, setHeaderDecision] = useState<AiBatchHeaderDecision | null>(null);
  const [normalizing, setNormalizing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [commitUnknown, setCommitUnknown] = useState(false);
  const [rowValidation, setRowValidation] = useState<ReturnType<typeof apiBatchRowError>>(undefined);
  const [fatal, setFatal] = useState<string | null>(null);
  const [done, setDone] = useState<CreateAiApplicationBatchResult | null>(null);
  const generationRef = useRef(0);
  const rowsRef = useRef(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => () => {
    generationRef.current += 1;
  }, []);

  const selected = rows.find((row) => row.id === selectedId) ?? rows[0] ?? null;
  const totals = useMemo(() => batchCurrencyTotals(rows), [rows]);
  const allReady = rows.length > 0 && rows.every((row) => deriveBatchRowStatus(row) === 'ready');
  const hasManualChanges = rows.some((row) => row.manualFields.size > 0);

  function patchRow(id: string, patch: (row: AiBatchEditableRow) => AiBatchEditableRow) {
    setRows((current) => current.map((row) => row.id === id ? patch(row) : row));
  }

  async function parseOne(id: string, input: string, generation: number, parentId = sourceRecordRef.current) {
    patchRow(id, (row) => ({ ...row, status: 'parsing', error: null }));
    try {
      const result = await aiExtractApplication(input, parentId);
      if (generationRef.current !== generation) return;
      patchRow(id, (row) => {
        const next = { ...row, result, error: null, status: 'pending' as const };
        return { ...next, status: deriveBatchRowStatus(next) };
      });
    } catch (err) {
      if (generationRef.current !== generation) return;
      patchRow(id, (row) => ({ ...row, status: 'failed', error: apiErrorMessage(err, 'AI 解析失败') }));
    }
  }

  async function parseQueue(nextRows: AiBatchEditableRow[], nextColumns: AiBatchWorkbookColumn[], generation: number) {
    const parentId = sourceRecordRef.current;
    const queue = nextRows.filter((row) => row.unsafeCells.length === 0);
    let cursor = 0;
    const worker = async () => {
      while (cursor < queue.length) {
        const row = queue[cursor++]!;
        await parseOne(row.id, serializeOriginalRow(nextColumns, row), generation, parentId);
      }
    };
    await Promise.all(Array.from({ length: Math.min(MAX_AI_CONCURRENCY, queue.length) }, worker));
  }

  async function confirmReplace(): Promise<boolean> {
    if (!hasManualChanges) return true;
    return new Promise((resolve) => {
      modal.confirm({
        title: '舍弃尚未提交的人工修改？',
        content: '重新读取会清空当前批次中的人工修改。',
        okText: '舍弃并继续',
        cancelText: '取消',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  async function installNormalized(
    normalized: ParsedPastedTable | AiBatchWorkbookResult,
    nextToken: string,
    decision: AiBatchHeaderDecision,
  ) {
    sourceRecordRef.current = decision.sourceRecordId;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const nextRows = editableRows(normalized);
    setColumns(normalized.columns);
    setRows(nextRows);
    setSelectedId(nextRows[0]?.id ?? null);
    setToken(nextToken);
    setHeaderDecision(decision);
    setFatal(null);
    setDone(null);
    setCommitUnknown(false);
    setRowValidation(undefined);
    void parseQueue(nextRows, normalized.columns, generation);
  }

  async function loadPasted(text = pasteText, replacing = true, html?: string) {
    if (normalizing || commitUnknown || submitting || !text.trim()) return;
    if (replacing && !(await confirmReplace())) return;
    setNormalizing(true);
    try {
      const source = await savePastedInput(text, html);
      const raw = parsePastedTable(text, false, 51);
      const decision = await aiDetectBatchHeader(aiHeaderSample(raw.columns, raw.rows), source.sourceRecordId);
      const normalized = applyAiHeaderDecision(raw, decision);
      const nextToken = await issueAiBatchToken();
      await installNormalized(normalized, nextToken, decision);
    } catch (err) {
      setFatal(err instanceof Error ? err.message : apiErrorMessage(err));
    } finally {
      setNormalizing(false);
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    if (normalizing || submitting || commitUnknown) {
      event.preventDefault();
      return;
    }
    const html = event.clipboardData.getData('text/html');
    const table = parseClipboardTableHtml(html);
    if (table) {
      event.preventDefault();
      setPasteText(table.text);
      setPasteNotice({ kind: 'table', rows: table.rowCount, columns: table.columnCount });
      // React 状态尚未刷新，直接传本次表格，避免解析旧的 pasteText。
      void loadPasted(table.text, true, html);
      return;
    }

    const plainText = event.clipboardData.getData('text/plain');
    setPasteNotice({ kind: 'text' });
    if (!plainText) return;
    event.preventDefault();
    const start = event.currentTarget.selectionStart ?? pasteText.length;
    const end = event.currentTarget.selectionEnd ?? start;
    setPasteText(`${pasteText.slice(0, start)}${plainText}${pasteText.slice(end)}`);
  }

  async function loadExcel(file: File, replacing = true) {
    if (commitUnknown || submitting) return;
    if (replacing && !(await confirmReplace())) return;
    setNormalizing(true);
    try {
      const raw = await parseAiBatchWorkbook(file, false);
      const decision = await aiDetectBatchHeader(aiHeaderSample(raw.columns, raw.rows), raw.sourceRecordId);
      const normalized = applyAiHeaderDecision(raw, decision);
      await installNormalized(normalized, normalized.token, decision);
    } catch (err) {
      setFatal(apiErrorMessage(err, 'Excel 读取失败'));
    } finally {
      setNormalizing(false);
    }
  }

  function removeRow(id: string) {
    if (rowValidation?.clientRowId === id) setRowValidation(undefined);
    setRows((current) => {
      const next = current.filter((row) => row.id !== id);
      if (selectedId === id) setSelectedId(next[0]?.id ?? null);
      return next;
    });
  }

  function retryRow(row: AiBatchEditableRow) {
    if (rowValidation?.clientRowId === row.id) setRowValidation(undefined);
    if (row.unsafeCells.length > 0) return;
    const generation = generationRef.current;
    void parseOne(row.id, serializeOriginalRow(columns, row), generation);
  }

  function changeResult(id: string, result: AiApplicationResult) {
    if (rowValidation?.clientRowId === id) setRowValidation(undefined);
    patchRow(id, (row) => {
      const next = { ...row, result, error: null, status: 'pending' as const };
      return { ...next, status: deriveBatchRowStatus(next) };
    });
  }

  function markManual(id: string, key: string) {
    patchRow(id, (row) => ({ ...row, manualFields: new Set(row.manualFields).add(key) }));
  }

  async function commit() {
    if (!allReady || !token) return;
    setSubmitting(true);
    // 旧的明确拒绝只属于上一次请求；本次响应可能进入结果未知。
    setRowValidation(undefined);
    try {
      const result = await createAiApplicationBatch({ ...buildAiBatchCreateInput(token, rowsRef.current), sourceRecordId: sourceRecordRef.current });
      setDone(result);
      setRowValidation(undefined);
      setCommitUnknown(false);
      message.success(result.duplicated ? '这一批已创建过，没有重复创建' : '批量创建成功');
    } catch (err) {
      const code = apiErrorCode(err);
      if (code && DEFINITE_REJECTION_CODES.has(code)) {
        if (code === 'AI_BATCH_ROW_INVALID') {
          const details = apiBatchRowError(err);
          const failedRow = rowsRef.current.find((row) => row.id === details?.clientRowId
            || row.rowNumber === details?.rowNumber);
          setRowValidation({ ...details, clientRowId: failedRow?.id });
          if (failedRow) setSelectedId(failedRow.id);
        }
        message.error(apiErrorMessage(err));
      } else {
        setCommitUnknown(true);
        modal.error({
          title: '这一批的结果还不确定',
          width: 520,
          content: '不要重新上传或粘贴。请先去打款申请页核对；如需重试，只能使用当前页面的同一批次号。',
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  function confirmCommit() {
    modal.confirm({
      title: `确认创建 ${rows.length} 个收款人与 ${rows.length} 张申请？`,
      content: (
        <Space orientation="vertical" size={8}>
          <Text>所有条目会在一个事务中创建，任意一条校验失败都不会写入。</Text>
          <Text strong>本操作只创建待打款申请，不会执行打款。</Text>
        </Space>
      ),
      okText: '确认创建',
      cancelText: '继续核对',
      onOk: commit,
    });
  }

  if (done) {
    return (
      <Result
        status="success"
        title={done.duplicated ? '这一批之前已经创建过' : `已创建 ${done.created.length} 个收款人与 ${done.created.length} 张申请`}
        subTitle="这里只创建了待打款申请，没有执行任何真实打款。"
        extra={[
          <Link key="applications" to="/payout-applications"><Button type="primary">去打款申请</Button></Link>,
          <Link key="payees" to="/payees"><Button>去收款人</Button></Link>,
        ]}
      >
        <Paragraph>新收款人短 ID：{done.created.map((item) => <Text code key={item.payeeId}>{item.payeeShortId}</Text>)}</Paragraph>
      </Result>
    );
  }

  return (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        title="AI 只负责解析并创建待打款申请，不会执行打款；内容会发送给第三方 AI 服务，推断或搜索补充的数据会明确标出。"
      />
      {fatal && <Alert type="error" showIcon title="无法读取这批数据" description={fatal} closable onClose={() => setFatal(null)} />}

      <div
        data-testid="ai-batch-ingest-layout"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 0.8fr) minmax(320px, 1.2fr)', gap: 12 }}
      >
        <div data-testid="ai-batch-upload" style={{ minHeight: 112 }}>
          <Upload.Dragger
            accept=".xlsx"
            maxCount={1}
            showUploadList={false}
            disabled={normalizing || submitting || commitUnknown}
            style={{ height: '100%' }}
            beforeUpload={(file) => {
              void loadExcel(file as unknown as File);
              return false;
            }}
          >
            <Space size={12} align="center" style={{ padding: '8px 12px', textAlign: 'start' }}>
              <InboxOutlined style={{ fontSize: 28 }} />
              <span>
                <Text strong style={{ display: 'block' }}>上传 Excel</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>点击或拖拽文件，自动识别表头</Text>
              </span>
            </Space>
          </Upload.Dragger>
        </div>
        <Card size="small" title="粘贴表格" styles={{ body: { padding: 12 } }}>
          <Input.TextArea
            value={pasteText}
            disabled={normalizing || submitting || commitUnknown}
            onChange={(event) => {
              setPasteText(event.target.value);
              setPasteNotice(null);
            }}
            onPaste={handlePaste}
            rows={3}
            placeholder="从多维表格或 Excel 复制后粘贴到这里；表格自动解析，纯文本请点击读取"
          />
          {pasteNotice?.kind === 'table' && (
            <Text type="success" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
              已识别剪贴板表格：{pasteNotice.rows} 行 × {pasteNotice.columns} 列；已保留空单元格和单元格内换行。
            </Text>
          )}
          {pasteNotice?.kind === 'text' && (
            <Text type="warning" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
              未检测到剪贴板表格格式，已填入纯文本；点击「读取粘贴内容」后才会解析。
            </Text>
          )}
          <Button
            type="primary"
            block
            style={{ marginTop: 8 }}
            loading={normalizing}
            disabled={!pasteText.trim() || normalizing || submitting || commitUnknown}
            onClick={() => void loadPasted()}
          >
            读取粘贴内容
          </Button>
        </Card>
      </div>

      {commitUnknown && (
        <Alert
          type="error"
          showIcon
          title="提交结果未知：已锁定上传与粘贴入口"
          description="只能使用当前批次号重试，避免换批次号后重复创建。"
        />
      )}

      {rows.length > 0 && (
        <>
          {headerDecision && (
            <Alert
              type={headerDecision.firstRowIsHeader ? 'success' : 'info'}
              showIcon
              title={headerDecision.firstRowIsHeader
                ? 'AI 已识别第一行为表头'
                : 'AI 判断第一行为数据'}
              description={`${headerDecision.reason}；本批共 ${rows.length} 条数据。`}
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '240px minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
            <Card data-testid="ai-batch-row-list" size="small" title="条目" styles={{ body: { padding: 8 } }}>
              <Space orientation="vertical" size={6} style={{ width: '100%' }}>
                {rows.map((row) => (
                  <Button
                    key={row.id}
                    data-testid="ai-batch-row"
                    type={selected?.id === row.id ? 'primary' : 'text'}
                    block
                    style={{ height: 'auto', paddingBlock: 8, textAlign: 'start' }}
                    onClick={() => setSelectedId(row.id)}
                    aria-label={`第 ${row.rowNumber} 行`}
                  >
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <span>第 {row.rowNumber} 行</span>{statusTag(row)}
                    </Space>
                  </Button>
                ))}
              </Space>
            </Card>

            {selected ? (
              <Card
                size="small"
                title={`第 ${selected.rowNumber} 行详情`}
                extra={(
                  <Space>
                    {selected.error && <Button icon={<ReloadOutlined />} disabled={normalizing || submitting || commitUnknown} onClick={() => retryRow(selected)}>重试解析</Button>}
                    <Button danger icon={<DeleteOutlined />} disabled={normalizing || submitting || commitUnknown} onClick={() => removeRow(selected.id)}>删除此条</Button>
                  </Space>
                )}
              >
                {rowValidation?.clientRowId === selected.id && (
                  <Alert
                    type="error"
                    showIcon
                    title="这一条未通过提交校验"
                    description={(
                      <Space orientation="vertical" size={4}>
                        <Text>{rowValidation.message}</Text>
                        {(rowValidation.fieldErrors ?? rowValidation.fields?.map((source) => ({ source, code: undefined })) ?? []).map((field) => (
                          <Text key={field.source}>
                            {selected.result?.payee.fields.find((item) => item.path === field.source)?.label ?? field.source}
                            {` · ${field.source}${field.code ? ` · ${field.code}` : ''}`}
                          </Text>
                        ))}
                        {rowValidation.airwallexCode && <Text>上游错误码：{rowValidation.airwallexCode}</Text>}
                        {rowValidation.requestId && <Text copyable>排查编号：{rowValidation.requestId}</Text>}
                        <Text>整批未创建，请修改后重新提交。</Text>
                      </Space>
                    )}
                    style={{ marginBottom: 12 }}
                  />
                )}
                {selected.error && <Alert type="error" showIcon title="这一条解析失败" description={selected.error} style={{ marginBottom: 12 }} />}
                {selected.unsafeCells.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    title="发现可能已丢失精度的数字单元格"
                    description="账号超过 15 位时，Excel 可能已经改写数字。请回到原表把账号列设置为文本后重新上传。"
                    style={{ marginBottom: 12 }}
                  />
                )}
                {selected.rowGroupingUncertain && !selected.groupingConfirmed && (
                  <Alert
                    type="warning"
                    showIcon
                    title="这一条的数据行由系统自动合并，分组不确定"
                    description={(
                      <Space orientation="vertical">
                        {selected.warnings.map((warning) => <Text key={warning}>{warning}</Text>)}
                        <Checkbox
                          disabled={normalizing || submitting || commitUnknown}
                          checked={selected.groupingConfirmed}
                          onChange={(event) => patchRow(selected.id, (row) => {
                            const next = { ...row, groupingConfirmed: event.target.checked, status: 'pending' as const };
                            return { ...next, status: deriveBatchRowStatus(next) };
                          })}
                        >
                          确认这一条的行分组正确
                        </Checkbox>
                      </Space>
                    )}
                    style={{ marginBottom: 12 }}
                  />
                )}
                <AiApplicationEditor
                  layout="split-review"
                  result={selected.result}
                  manualFields={selected.manualFields}
                  showInputSources
                  validationFields={rowValidation?.clientRowId === selected.id ? rowValidation.fields : undefined}
                  disabled={normalizing || commitUnknown || submitting}
                  onResultChange={(result) => changeResult(selected.id, result)}
                  onManualField={(key) => markManual(selected.id, key)}
                  payeeEmptyDescription={selected.status === 'parsing' ? 'AI 正在解析收款人…' : '暂无解析结果'}
                  applicationEmptyDescription={selected.status === 'parsing' ? 'AI 正在解析申请…' : '暂无解析结果'}
                  original={(
                    <>
                      <Title level={5} style={{ marginTop: 0 }}>1. 原始信息</Title>
                      <Paragraph type="secondary" style={{ fontSize: 12 }}>按原表列顺序平铺，只读展示，不会被 AI 结果覆盖。</Paragraph>
                      <Descriptions
                        size="small"
                        bordered
                        column={1}
                        items={columns.map((column) => ({
                          key: column.key,
                          label: `${column.excelColumn} · ${column.title}`,
                          children: selected.cells[column.key] || <Text type="secondary">—</Text>,
                        }))}
                      />
                    </>
                  )}
                />
              </Card>
            ) : <Empty description="这批数据已全部删除" />}
          </div>

          <Divider style={{ marginBlock: 0 }} />
          <div style={{ display: 'flex', gap: 16, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <Space wrap>
              <Text strong>批次合计</Text>
              {totals.length === 0
                ? <Text type="secondary">等待有效金额</Text>
                : totals.map((total) => <Tag key={total.currency}>{total.count} 笔 · {total.amount} {total.currency}</Tag>)}
            </Space>
            <Button
              type="primary"
              size="large"
              loading={submitting}
              disabled={!allReady || normalizing || submitting}
              onClick={confirmCommit}
            >
              {commitUnknown ? '重试同一批次' : `创建 ${rows.length} 个收款人与 ${rows.length} 张申请`}
            </Button>
          </div>
        </>
      )}
    </Space>
  );
}
